import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Table,
  Button,
  Input,
  Segment,
  Header,
  Icon,
  Loader,
  Message,
  Confirm,
  Label,
} from "semantic-ui-react";
import {
  SG_GET_LLM_MODEL_CONFIGS,
  SG_DELETE_LLM_MODEL_CONFIG,
} from "../constants/actions";

export default function ModelConfigList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState("");

  const {
    llm_model_configs = [],
    llm_model_configs_pending = false,
    llm_model_configs_error,
    llm_model_configs_total = 0,
  } = useSelector((state) => state.llm_log || {});

  useEffect(() => {
    const params = {};
    if (search) params.search = search;

    dispatch({
      type: SG_GET_LLM_MODEL_CONFIGS,
      payload: { queryParams: params },
    });
  }, [dispatch, search]);

  const handleDelete = () => {
    if (selectedId) {
      dispatch({
        type: SG_DELETE_LLM_MODEL_CONFIG,
        payload: { id: selectedId },
      });
      setDeleteConfirm(false);
      setSelectedId(null);
    }
  };

  const handleEdit = (id) => {
    navigate(`/app/llm-usage/model-configs/edit/${id}`);
  };

  return (
    <div>
      <Segment>
        <Header as="h2">
          <Icon name="cog" />
          <Header.Content>
            LLM Model Configurations
            <Header.Subheader>Manage LLM model settings and pricing</Header.Subheader>
          </Header.Content>
        </Header>

        <div style={{ marginBottom: "20px", display: "flex", gap: "10px" }}>
          <Input
            icon="search"
            placeholder="Search by model name or provider..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1 }}
          />
          <Button
            primary
            icon
            labelPosition="left"
            onClick={() => navigate("/app/llm-usage/model-configs/create")}
          >
            <Icon name="plus" />
            Add Model Config
          </Button>
        </div>

        {llm_model_configs_error && (
          <Message negative>
            <Message.Header>Error</Message.Header>
            <p>{llm_model_configs_error}</p>
          </Message>
        )}

        {llm_model_configs_pending ? (
          <Loader active inline="centered">
            Loading...
          </Loader>
        ) : (
          <>
            <Table celled striped>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Model Name</Table.HeaderCell>
                  <Table.HeaderCell>Provider</Table.HeaderCell>
                  <Table.HeaderCell>Scope</Table.HeaderCell>
                  <Table.HeaderCell>Prompt Cost</Table.HeaderCell>
                  <Table.HeaderCell>Completion Cost</Table.HeaderCell>
                  <Table.HeaderCell>Health</Table.HeaderCell>
                  <Table.HeaderCell>Status</Table.HeaderCell>
                  <Table.HeaderCell>Actions</Table.HeaderCell>
                </Table.Row>
              </Table.Header>

              <Table.Body>
                {llm_model_configs.length === 0 ? (
                  <Table.Row>
                    <Table.Cell colSpan="8" textAlign="center">
                      <Icon name="inbox" size="large" />
                      <p>No model configurations found</p>
                    </Table.Cell>
                  </Table.Row>
                ) : (
                  llm_model_configs.map((config) => (
                    <Table.Row key={config.id}>
                      <Table.Cell>
                        <strong>{config.model_name}</strong>
                        {config.is_default && (
                          <Label size="mini" color="blue" style={{ marginLeft: 8 }}>
                            Default
                          </Label>
                        )}
                      </Table.Cell>
                      <Table.Cell>{config.provider}</Table.Cell>
                      <Table.Cell>
                        {config.organization ? (
                          <Label size="small" color="teal">
                            Org #{config.organization}
                          </Label>
                        ) : (
                          <Label size="small" basic>
                            Global
                          </Label>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        {config.price_prompt_per_1k_micro_usd || 0} μUSD
                        <br />
                        <small style={{ color: "#666" }}>
                          ${((config.price_prompt_per_1k_micro_usd || 0) / 1000000).toFixed(6)}
                        </small>
                      </Table.Cell>
                      <Table.Cell>
                        {config.price_completion_per_1k_micro_usd || 0} μUSD
                        <br />
                        <small style={{ color: "#666" }}>
                          ${((config.price_completion_per_1k_micro_usd || 0) / 1000000).toFixed(6)}
                        </small>
                      </Table.Cell>
                      <Table.Cell>
                        <Label
                          size="small"
                          color={
                            config.health_status === "ok"
                              ? "green"
                              : config.health_status === "degraded"
                              ? "orange"
                              : "red"
                          }
                        >
                          {config.health_status || "unknown"}
                        </Label>
                      </Table.Cell>
                      <Table.Cell>
                        {config.active ? (
                          <Label color="green" size="small">
                            Active
                          </Label>
                        ) : (
                          <Label color="grey" size="small">
                            Inactive
                          </Label>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        <Button
                          icon
                          size="small"
                          onClick={() => handleEdit(config.id)}
                        >
                          <Icon name="edit" />
                        </Button>
                        <Button
                          icon
                          size="small"
                          color="red"
                          onClick={() => {
                            setSelectedId(config.id);
                            setDeleteConfirm(true);
                          }}
                        >
                          <Icon name="trash" />
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                  ))
                )}
              </Table.Body>
            </Table>

            <div style={{ marginTop: "10px", color: "#666" }}>
              Total: {llm_model_configs_total} configuration(s)
            </div>
          </>
        )}
      </Segment>

      <Confirm
        open={deleteConfirm}
        onCancel={() => setDeleteConfirm(false)}
        onConfirm={handleDelete}
        content="Are you sure you want to delete this model configuration?"
      />
    </div>
  );
}